<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core';
import { computed, nextTick, onActivated, onDeactivated, ref, watch } from 'vue';
import type { Notification } from '../queue';
import { queue } from '../queue';
import { useNotifications } from '../useNotifications';

const props = defineProps<{
  name: keyof typeof queue;
  group?: string;
  limit?: number;
  pageSize?: number;
  scrollContainer?: HTMLElement;
}>();

const emit = defineEmits<{
  (e: 'remove', notification: Notification): void;
  (e: 'click', notification: Notification, preventClose: boolean): void;
}>();

let blockScroll = false;

onDeactivated(() => {
  blockScroll = true;
});
onActivated(() => {
  blockScroll = false;
});

const defaultPageSize = 20;
const pageSize = computed(() => props.pageSize ?? defaultPageSize);

const currentIndex = ref(0);
const scrollContainerRef = ref<HTMLElement | null>(null);

const increment = computed(() => Math.floor(pageSize.value / 2));
const maxIndex = computed(() => Math.max(0, filteredNotifications.value.length - pageSize.value));

const isLoading = ref(false);

const loadMore = async (direction: 'up' | 'down') => {
  if (filteredNotifications.value.length <= pageSize.value || isLoading.value) {
    return;
  }

  isLoading.value = true;

  try {
    const newIndex =
      direction === 'down'
        ? Math.min(maxIndex.value, currentIndex.value + increment.value)
        : Math.max(0, currentIndex.value - increment.value);

    if (
      currentIndex.value === newIndex ||
      (direction === 'down' && currentIndex.value >= maxIndex.value) ||
      (direction === 'up' && currentIndex.value <= 0)
    ) {
      return;
    }

    if (!scrollContainerRef.value) {
      currentIndex.value = newIndex;
      return;
    }

    const container = scrollContainerRef.value;
    const visibleElements = Array.from(container.getElementsByClassName('list-transition')).filter(
      (el) => {
        const rect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;
      },
    );

    const firstVisibleElement = visibleElements[0];
    if (!firstVisibleElement) {
      currentIndex.value = newIndex;
      return;
    }

    const relativeTop =
      firstVisibleElement.getBoundingClientRect().top - container.getBoundingClientRect().top;

    container.style.setProperty('--list-move-duration', '0s');
    currentIndex.value = newIndex;
    await nextTick();

    const newTop =
      firstVisibleElement.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTop += newTop - relativeTop;

    requestAnimationFrame(() => {
      container.style.removeProperty('--list-move-duration');
    });
  } finally {
    isLoading.value = false;
  }
};

const setupInfiniteScroll = () => {
  if (!props.scrollContainer) return;

  scrollContainerRef.value = props.scrollContainer;

  useInfiniteScroll(
    scrollContainerRef,
    () => {
      if (!blockScroll) {
        loadMore('down');
      }
    },
    { distance: 100, direction: 'bottom' },
  );

  useInfiniteScroll(
    scrollContainerRef,
    () => {
      if (!blockScroll) {
        loadMore('up');
      }
    },
    {
      distance: 100,
      direction: 'top',
    },
  );
};

watch(
  () => props.scrollContainer,
  (newContainer) => {
    if (newContainer) {
      setupInfiniteScroll();
    }
  },
  { immediate: true },
);

const filteredNotifications = computed(() =>
  queue[props.name].filter((n) => {
    if (!props.group) {
      return true;
    }

    const groups = n.options?.group?.split('|') ?? [];
    return groups.includes(props.group);
  }),
);

const notifications = computed(() => {
  if (!props.scrollContainer) {
    return filteredNotifications.value.slice(0, pageSize.value);
  }

  const start = currentIndex.value;
  const end = start + pageSize.value;
  return filteredNotifications.value.slice(start, end);
});

const onRemove = (notification: Notification) => {
  if (props.scrollContainer && currentIndex.value > 0) {
    if (currentIndex.value >= maxIndex.value) {
      currentIndex.value = Math.max(0, currentIndex.value - 1);
    }
  }

  useNotifications().remove(props.name, notification);
  emit('remove', notification);
};
</script>
<template>
  <TransitionGroup
    name="list"
    tag="div"
    class="v-move-group"
    :style="{ 'transform-style': 'flat' }"
  >
    <template v-for="(notification, key) in notifications" :key="notification.options?.id">
      <div v-if="limit === undefined || key < limit" class="list-transition">
        <Component
          :is="notification.component"
          v-bind="notification.props"
          @remove="onRemove(notification)"
          @click="emit('click', notification, $event)"
        />
      </div>
      <slot v-if="key !== notifications.length - 1" name="divider" />
    </template>
  </TransitionGroup>
  <slot v-if="notifications.length === 0" name="empty" />
</template>

<style scoped>
.list-move {
  transition: transform var(--list-move-duration, 0.3s) ease;
}

.list-enter-active,
.list-leave-active {
  transition: opacity 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
}

.list-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  transform: translateY(-100%);
}

.v-move-group {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.list-transition {
  min-height: inherit;
  position: relative;
  width: 100%;
}
</style>
